import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MachineModelDto, MachineModelsStore } from 'stores/configuration/machinery/machine-models-store';
import { nameof } from 'utils/object';
import TailwindMultiSelect from '../../editors/tailwind-multiselect';
import { connect } from 'redux-scaffolding-ts';

interface MultipleMachineModelEditorProps {
  value: MachineModelDto[];
  onChange?: (value: MachineModelDto[]) => void;
  machineModels?: MachineModelsStore;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  equipmentId?: string;
  clusterId?: string;
  oemId?: string;
  loadDataOnOpen?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

const MultipleMachineModelEditor: React.FC<MultipleMachineModelEditorProps> = ({
  value,
  onChange,
  machineModels,
  placeholder,
  clearable = true,
  disabled = false,
  className,
  equipmentId,
  clusterId,
  oemId,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<MachineModelDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getMachineModelMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      const filters = ['active eq true'];

      if (clusterId) filters.push(`${nameof<MachineModelDto>('clusterId')} eq ${clusterId}`);
      if (equipmentId) filters.push(`${nameof<MachineModelDto>('equipmentTypeId')} eq ${equipmentId}`);
      if (oemId) {
        filters.push(`${nameof<MachineModelDto>('oemId')} eq ${oemId}`);
      }

      const result = await machineModels.getAllAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<MachineModelDto>('name'), useProfile: false }],
        filter: filters
      });

      setOptions(result.items || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [clusterId, equipmentId, machineModels, oemId]);

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getMachineModelMethod();
    }
  }, [loadDataOnOpen, options.length, getMachineModelMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getMachineModelMethod();
    }
  }, [loadDataOnOpen, getMachineModelMethod]); // Re-fetch cuando cambien estos valores

  return (
    <TailwindMultiSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(machine => ({
              id: machine.id,
              text: machine.name
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={value?.map(v => ({
        id: v.id,
        text: v.name
      }))}
      onChange={newValue => {
        const selectedMachines = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
        onChange(selectedMachines);
      }}
      placeholder={placeholder || t('Select machine models')}
      clearable={clearable}
      disabled={disabled}
      className={className}
      onBlur={onBlur}
      onFocus={onFocus}
      searchable={!isLoading}
      onOpenDropDown={onOpenDropDown}
    />
  );
};

export default connect(['machineModels', MachineModelsStore])(MultipleMachineModelEditor);
